import {
  Button,
  IButtonProps as IBaseButtonProps,
  TButtonSizes,
} from '@private/components';
import { getBaseThemeProps, getPrefixedVar } from '@private/payment';
import styled, { css } from 'styled-components';

import Tooltip from 'components/Tooltip';
import { ThemedElement } from 'utils/types';

export enum BUTTON_PREVIEW_STATE {
  BASE = 'BASE',
  DISABLED = 'DISABLED',
  FOCUSED = 'FOCUSED',
  HOVERED = 'HOVERED',
  PRESSED = 'PRESSED',
}

export interface IButtonProps extends Omit<IBaseButtonProps, 'size'> {
  closeTooltipIn?: number;
  copyText?: string;
  enableCopyToClipboard?: boolean;
  enableShowFocus?: boolean;
  hideCopyIcon?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  loaderSize?: number;
  previewState?: BUTTON_PREVIEW_STATE;
  showTooltipOnClick?: boolean;
  size?: 'xxs' | TButtonSizes;
  tooltipDisabled?: boolean;
  tooltipText?: string;
}

interface IStyledButtonProps {
  $enableShowFocus?: IButtonProps['enableShowFocus'];
  $isActive?: IButtonProps['isActive'];
  $isLoading?: IButtonProps['isLoading'];
  $previewState?: IButtonProps['previewState'];
}

export const getBaseButtonProps = (prefix: string[]) => {
  return css`
    ${getBaseThemeProps(prefix)};

    background: ${getPrefixedVar(prefix, 'bg')};
    box-shadow:
      0 0 0 transparent,
      0 0 0 ${getPrefixedVar(prefix, 'outlineWidth')}
        ${getPrefixedVar(prefix, 'outlineColor')},
      0 5px 10px ${getPrefixedVar(prefix, 'boxShadow')};
  `;
};

type TButtonCombinedProps = ThemedElement<IButtonProps & IStyledButtonProps>;

export const getButtonTheme = (prefix: string[]) => {
  return css<TButtonCombinedProps>`
    ${getBaseButtonProps([...prefix, 'base'])};

    ${({ $isActive }) =>
      $isActive &&
      css`
        ${getBaseButtonProps([...prefix, 'focus'])};
      `}

    &:focus${({ $enableShowFocus }) => !$enableShowFocus && '-visible'} {
      ${getBaseButtonProps([...prefix, 'focus'])};
    }

    &:hover {
      ${getBaseButtonProps([...prefix, 'hover'])};
    }

    &:active {
      ${getBaseButtonProps([...prefix, 'pressed'])};
    }

    &:disabled {
      opacity: 1;
      ${getBaseButtonProps([...prefix, 'disabled'])};
    }

    ${({ $isLoading, variant }) =>
      $isLoading &&
      css`
        svg * {
          fill: ${getPrefixedVar(prefix, 'base', variant === 'icon' ? 'text' : 'bg')};
        }
      `}
  `;
};

export const StyledButton = styled(Button)<TButtonCombinedProps>`
  border-style: solid;
  min-height: 48px;
  line-height: 150%;

  svg {
    color: inherit;
  }

  ${({ size }) => {
    switch (size) {
      case 'lg':
        return css`
          min-height: 48px;
          min-width: 48px;
          padding: 0 20px;
        `;
      case 'md':
        return css`
          min-height: 40px;
          min-width: 40px;
          padding: 0 15px;
        `;
      case 'sm':
        return css`
          min-height: 36px;
          min-width: 36px;
          padding: 0 12px;
        `;
      case 'xs':
        return css`
          min-height: 32px;
          min-width: 32px;
          padding: 0 10px;
        `;
      case 'xxs':
        return css`
          min-height: 16px;
          min-width: 16px;
          padding: 0;
        `;
      default:
        return css`
          min-height: 48px;
          min-width: 48px;
          padding: 0 20px;
        `;
    }
  }};

  ${({ variant }) => {
    if (variant === 'link') {
      return css`
        min-height: 24px;
        padding: 0 10px;
      `;
    }

    if (variant === 'icon' || variant === 'iconOutlined' || variant === 'primaryIcon') {
      return css`
        padding: 0;
      `;
    }
  }}

  ${({ $previewState, themePrefix, variant = 'primary' }) => {
    const buttonVariant = variant === 'iconOutlined' ? 'outlined' : variant;
    const prefix = themePrefix || ['components', 'button', buttonVariant as string];

    if ($previewState) {
      const primaryPrefix = ['components', 'button', variant as string];

      switch ($previewState) {
        case BUTTON_PREVIEW_STATE.BASE:
          return css`
            ${getBaseButtonProps([...primaryPrefix, 'base'])};

            &:hover,
            &:active,
            &:focus {
              ${getBaseButtonProps([...primaryPrefix, 'base'])};
            }
          `;
        case BUTTON_PREVIEW_STATE.DISABLED:
          return css`
            ${getBaseButtonProps([...primaryPrefix, 'disabled'])};

            &:hover,
            &:active,
            &:focus {
              ${getBaseButtonProps([...primaryPrefix, 'disabled'])};
            }
          `;
        case BUTTON_PREVIEW_STATE.FOCUSED:
          return css`
            ${getBaseButtonProps([...primaryPrefix, 'focus'])};

            &:hover,
            &:active,
            &:focus {
              ${getBaseButtonProps([...primaryPrefix, 'focus'])};
            }
          `;
        case BUTTON_PREVIEW_STATE.HOVERED:
          return css`
            ${getBaseButtonProps([...primaryPrefix, 'hover'])}

            &:hover, &:active, &:focus {
              ${getBaseButtonProps([...primaryPrefix, 'hover'])}
            }
          `;
        case BUTTON_PREVIEW_STATE.PRESSED:
          return css`
            ${getBaseButtonProps([...primaryPrefix, 'pressed'])};

            &:hover,
            &:active,
            &:focus {
              ${getBaseButtonProps([...primaryPrefix, 'pressed'])};
            }
          `;
        default:
          return;
      }
    }

    return getButtonTheme(prefix);
  }}
`;

export const StyledTooltip = styled(Tooltip)`
  z-index: 1501;
`;
